import { useKeycloak } from "@react-keycloak/web";
import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../app/store";
import { AUTH_LOCALSTORAGE_NAME } from "../constants/constants";
import { setAuthInfo, userLoggedIn } from "../features/auth/authSlice";
import { useGetMeQuery } from "../features/employee/employeeApi";
import useError from "./useError";
import useSetLocalProduct from "./useSetLocalProduct";

export default function useAppBoot() {
  const { access_token, authInfo } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const { keycloak, initialized } = useKeycloak();
  const loading = useSetLocalProduct();
  const [search] = useSearchParams();
  const tokenEmailVerified = Boolean(
    (keycloak?.tokenParsed as { email_verified?: boolean } | undefined)
      ?.email_verified
  );

  // Sync Keycloak token with Redux on initialization
  useEffect(() => {
    if (initialized && keycloak?.token) {
      // Get from localStorage first as backup
      const storedAuth = localStorage.getItem(AUTH_LOCALSTORAGE_NAME);
      const storedToken = storedAuth
        ? JSON.parse(storedAuth)?.access_token
        : null;

      // Use Keycloak token or fall back to stored token
      const token = keycloak.token || storedToken;

      if (token) {
        dispatch(userLoggedIn({ access_token: token }));
        dispatch(
          setAuthInfo({
            email_verified: tokenEmailVerified,
          })
        );
      }
    }
  }, [dispatch, initialized, keycloak?.token, tokenEmailVerified]);

  const {
    data: userData,
    isLoading: userLoading,
    isError: isEmployeeDetailsError,
    error: employeeDetailsError,
  } = useGetMeQuery(undefined, {
    skip: !access_token,
  });

  useError(
    isEmployeeDetailsError,
    employeeDetailsError,
    "Get employee details fail",
  );

  const employeeInfo = userData?.data?.employee;

  useEffect(() => {
    if (!employeeInfo) {
      return;
    }

    dispatch(
      setAuthInfo({
        role: employeeInfo?.type,
        id: employeeInfo?.id,
        business_id: employeeInfo?.business_id,
        profile_id: employeeInfo?.member?.profile?.id,
        email: employeeInfo?.member?.profile?.email,
        email_verified: Boolean(employeeInfo?.member?.profile?.email_verified),
        departmentId: employeeInfo?.role?.department?.id,
        designationName: employeeInfo?.role?.name,
      }),
    );
  }, [
    dispatch,
    employeeInfo,
    employeeInfo?.business_id,
    employeeInfo?.id,
    employeeInfo?.member?.profile?.email,
    employeeInfo?.member?.profile?.email_verified,
    employeeInfo?.member?.profile?.id,
    employeeInfo?.role?.department?.id,
    employeeInfo?.role?.name,
    employeeInfo?.type,
  ]);

  useEffect(() => {
    const type = search.get("type");
    const dynamicId = search.get("dynamic-id");
    const dynamicKeyword = search.get("dynamic-keyword");

    if (type) {
      localStorage.setItem("product-type", JSON.stringify(type));
    }

    if (dynamicId && dynamicKeyword) {
      localStorage.setItem(
        "dynamic",
        JSON.stringify({
          dynamicId,
          dynamicKeyword,
        }),
      );
    }
  }, [search]);

  const navigateUrl =
    authInfo?.role === "Admin" || authInfo?.role === "Manager"
      ? "/admin/dashboard"
      : "/admin/user-dashboard";

  const isAppLoading = !initialized || userLoading || loading;

  return { loading: isAppLoading, navigateUrl };
}
